import { FormEvent, useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'

import { useAuth } from 'auth/AuthContext'
import { Button, PasswordField, TextField } from 'components/ui'
import { errorMessage } from 'lib/hooks'

import { AuthShell } from './AuthShell'
import { postAuthPath } from './postAuthRedirect'

const COPY = {
  ar: {
    title: 'تسجيل الدخول',
    subtitle: 'استخدم بريد العمل الخاص بك للوصول إلى مشاريعك.',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    remember: 'تذكّرني',
    forgot: 'نسيت كلمة المرور؟',
    submit: 'تسجيل الدخول',
    submitBusy: 'جارٍ تسجيل الدخول…',
    footer: 'هل تواجه مشكلة في تسجيل الدخول؟ تواصل مع مدير مشروعك.',
    errorTitle: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
    errorBody: 'تحقق من البيانات وحاول مرة أخرى.',
  },
  en: {
    title: 'Sign in',
    subtitle: 'Use your work email to open your projects.',
    email: 'Email',
    password: 'Password',
    remember: 'Remember me',
    forgot: 'Forgot password?',
    submit: 'Sign in',
    submitBusy: 'Signing in…',
    footer: 'Trouble signing in? Contact your project administrator.',
    errorTitle: 'Incorrect email or password',
    errorBody: 'Check your details and try again.',
  },
} as const

export const SignInPage = () => {
  const { user, signIn } = useAuth()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (user) {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={postAuthPath(user, from)} replace />
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await signIn(email, password, remember)
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell>
      {(locale) => {
        const t = COPY[locale]
        return (
          <>
            <span className="auth__wordmark">JoPath</span>
            <div className="auth__head">
              <h1>{t.title}</h1>
              <p>{t.subtitle}</p>
            </div>
            <form className="stack" onSubmit={submit} noValidate>
              {error ? (
                <div className="alert alert--error" role="alert">
                  <div>
                    <strong>{t.errorTitle}</strong>
                    <p>{t.errorBody}</p>
                  </div>
                </div>
              ) : null}
              <TextField
                label={t.email}
                type="email"
                autoComplete="username"
                dir="ltr"
                value={email}
                disabled={busy}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
              <PasswordField
                label={t.password}
                autoComplete="current-password"
                dir="ltr"
                value={password}
                disabled={busy}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
              <div className={busy ? 'auth__options is-disabled' : 'auth__options'}>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(event) => setRemember(event.target.checked)}
                  />
                  {t.remember}
                </label>
                <Link to="/forgot-password" className="link-button">
                  {t.forgot}
                </Link>
              </div>
              <Button
                type="submit"
                variant="primary"
                busy={busy}
                disabled={!email || !password}
                className="btn--block"
              >
                {busy ? t.submitBusy : t.submit}
              </Button>
            </form>
            <p className="auth__footer">{t.footer}</p>
          </>
        )
      }}
    </AuthShell>
  )
}
